import { TextField, useFormContext, useFormField } from '@calendly/form'
import { HtmlParser } from '@calendly/html-parser'
import { I18n } from '@calendly/i18n'
import { Banner } from '@calendly/ui/components/banner'
import { FlexLayout } from '@calendly/ui/components/flex-layout'
import { FullStoryExclude } from '@calendly/ui/components/fullstory-privacy'
import { ProTip } from '@calendly/ui/components/pro-tip'
import { Control, Option, Options, Select } from '@calendly/ui/components/select'
import { BodyText } from '@calendly/ui/components/typography'
import { useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events/constants'
import { useTrack } from 'hooks/analytics_events/useTrack'

import { countriesWithNoPostalCode } from './countries_with_no_postal_code'
import { clearInvalidFormFields } from './slice'
import {
  AussieText,
  HorizontalField,
  HorizontalFieldsWrapper,
} from './styles'
import {
  dealsPageAvailableCountriesSelector,
  dealsPageStatesByCountrySelector,
  policyAvailableCountriesSelector,
  policyStatesByCountrySelector,
  taxInformationModalConfirmedTaxInformationSelector,
  taxInformationModalInvalidFormFieldsSelector,
  taxInformationModalPopupLocation,
  taxInformationModalShouldUpdateExistingSubscriptionSelector
} from '../../selectors'
import { secondaryTaxIdSelector, taxIdSelector } from '../../stages/paid_stage/billing_information_card/selectors'


const CountrySelect = () => {
  const [_, { value: selected }, { setValue: setSelected }] = useFormField('countryCode')
  const [__, ___, { setValue: setSelectedStateProvince }] = useFormField('stateProvince')
  const policyAvailableCountries = useSelector(policyAvailableCountriesSelector)
  const dealsAvailableCountries = useSelector(dealsPageAvailableCountriesSelector)

  const availableCountries = () => policyAvailableCountries || dealsAvailableCountries

  return (
    <>
      <BodyText weight="bold">
        {I18n.t('publisher.billing.vat_id_popup.country_territory')}
      </BodyText>

      <Select
        onChange={(e) => {
          setSelectedStateProvince('') // Reset state/province so it doesn't linger on form values and cause 400s on submit
          setSelected(e)
        }}
        spacingTop="8"
        value={selected}
      >
        <Control placeholder={I18n.t('publisher.billing.vat_id_popup.select_a_country_territory')}>
          {selected && availableCountries().find((ac) => ac.id === selected).name}
        </Control>
        <Options>
          {availableCountries().map((ac) => (
            <Option
              key={`vat-popup-country-${ac.id}`}
              value={ac.id}
            >
              {ac.name}
            </Option>
          ))}
        </Options>
      </Select>
    </>
  )
}

const StateSelect = () => {
  const [_, { value: selected }, { setValue: setSelected }] = useFormField('stateProvince')
  const [__, { value: selectedCountry }] = useFormField('countryCode')
  const policyStatesByCountry = useSelector(policyStatesByCountrySelector)
  const dealsStatesByCountry = useSelector(dealsPageStatesByCountrySelector)

  const statesByCountry = () => policyStatesByCountry || dealsStatesByCountry

  const availableStates = statesByCountry()[selectedCountry]

  return (
    <>
      <BodyText weight="bold">
        {I18n.t('publisher.billing.vat_id_popup.state_province')}
      </BodyText>

      <Select
        onChange={(e) => setSelected(e)}
        spacingTop="8"
        value={selected}
      >
        <Control placeholder={I18n.t('publisher.billing.vat_id_popup.state_province')}>
          {selected && availableStates.find((as) => as.id === selected).name}
        </Control>
        <Options>
          {availableStates.map((as) => (
            <Option
              key={`vat-popup-state-province-${as.id}`}
              value={as.id}
            >
              {as.name}
            </Option>
          ))}
        </Options>
      </Select>
    </>
  )
}

const AddressInput = () => {
  const [_, { value: selectedCountry }] = useFormField('countryCode')

  return (
    <FlexLayout direction="column" gap="8">
      <div>
        {
        countriesWithNoPostalCode.includes(selectedCountry) ? (
          <CountrySelect />
        ) : (
          <HorizontalFieldsWrapper>
            <HorizontalField>
              <CountrySelect />
            </HorizontalField>

            <HorizontalField>
              <FullStoryExclude>
                <TextField
                  name="postalCode"
                  label={I18n.t('publisher.billing.vat_id_popup.postal_code')}
                  spacingTop="8"
                  autoComplete="postal-code"
                  disabled={selectedCountry === 'US'}
                />
              </FullStoryExclude>
            </HorizontalField>
          </HorizontalFieldsWrapper>
        )
        }
      </div>

      <div>
        <FullStoryExclude>
          <TextField
            name="streetAddress"
            label={I18n.t('publisher.billing.vat_id_popup.street_address')}
            spacingTop="8"
            autoComplete="street-address"
            disabled={selectedCountry === 'US'}
          />
        </FullStoryExclude>
      </div>

      <div>
        <FullStoryExclude>
          <TextField
            name="streetAddressTwo"
            spacingTop="8"
            label={(
              <BodyText weight="bold">
                {I18n.t('publisher.billing.vat_id_popup.street_address_two')}
                {' '}
                <BodyText inline color="muted">
                  {I18n.t('publisher.billing.vat_id_popup.optional')}
                </BodyText>
              </BodyText>
            )}
            autoComplete="address-line2"
            disabled={selectedCountry === 'US'}
          />
        </FullStoryExclude>
      </div>

      {selectedCountry === 'CA' || selectedCountry === 'IN' ? (
        <div>
          <HorizontalFieldsWrapper>
            <HorizontalField>
              <FullStoryExclude>
                <TextField
                  name="city"
                  label={I18n.t('publisher.billing.vat_id_popup.city')}
                  spacingTop="8"
                  autoComplete="address-level2"
                  disabled={selectedCountry === 'US'}
                />
              </FullStoryExclude>
            </HorizontalField>

            <HorizontalField>
              <StateSelect />
            </HorizontalField>
          </HorizontalFieldsWrapper>
        </div>
      ) : (
        <div>
          <FullStoryExclude>
            <TextField
              name="city"
              label={I18n.t('publisher.billing.vat_id_popup.city')}
              spacingTop="8"
              autoComplete="address-level2"
              disabled={selectedCountry === 'US'}
            />
          </FullStoryExclude>
        </div>
      )}
    </FlexLayout>
  )
}

export const TaxForm = () => {
  const [_, { value: selectedCountry }] = useFormField('countryCode')
  const [__, { value: selectedStateProvince }] = useFormField('stateProvince')
  const [___, { touched: provincialChanged, value: canadaProvincialTaxId }] = useFormField('canadaProvincialTaxId')
  const invalidFormFields = useSelector(taxInformationModalInvalidFormFieldsSelector)
  const { errors, setFieldError } = useFormContext()
  const taxId = useSelector(taxIdSelector)
  const secondaryTaxId = useSelector(secondaryTaxIdSelector)
  const taxIdPresent = taxId || secondaryTaxId
  const dispatch = useDispatch()
  const track = useTrack()
  const hasErrors = errors && Object.keys(errors).length > 0
  const previousInvalid = useRef({ invalidFormFields: [] }).current
  const invalidChanged = invalidFormFields !== previousInvalid.invalidFormFields
  const invalidAndChanged = invalidFormFields.length > 0 && invalidChanged
  const errorsNoInvalid = hasErrors && !invalidChanged
  const shouldUpdateExistingSubscription = useSelector(taxInformationModalShouldUpdateExistingSubscriptionSelector)
  const confirmedTaxInfo = useSelector(taxInformationModalConfirmedTaxInformationSelector)
  const popupLocation = useSelector(taxInformationModalPopupLocation)
  const showCanadaProvincialTaxId = selectedStateProvince === 'QC' || !!canadaProvincialTaxId || provincialChanged

  useEffect(() => {
    if (invalidAndChanged) {
      track({
        name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
        properties: {
          action: 'confirming_info_error',
          location: 'billing_page',
          modal_name: 'confirm_tax_info',
        }
      })
      dispatch(clearInvalidFormFields())
    } else if (errorsNoInvalid) {
      const action = () => {
        if (shouldUpdateExistingSubscription) {
          return taxIdPresent ? 'saving_edited_info_error' : 'submitting_info_error'
        }
        return confirmedTaxInfo ? 'saving_edited_info_error' : 'submitting_info_error'
      }

      const modalName = () => {
        if (shouldUpdateExistingSubscription) {
          return taxIdPresent ? 'edit_tax_info' : 'add_tax_info'
        }
        return confirmedTaxInfo ? 'edit_tax_info' : 'add_tax_info'
      }

      track({
        name: TRACKING_EVENT_NAMES.MODAL_INTERACTION,
        properties: {
          action: action(),
          location: popupLocation,
          modal_name: modalName(),
        }
      })
    }

    previousInvalid.invalidFormFields = invalidFormFields
  }, [
    confirmedTaxInfo,
    errors,
    invalidAndChanged,
    invalidFormFields,
    errorsNoInvalid,
    popupLocation,
    taxIdPresent,
    dispatch,
    previousInvalid,
    shouldUpdateExistingSubscription,
    track
  ])

  useEffect(() => {
    const errorMessageByField = {
      australiaTaxId: I18n.t('publisher.billing.vat_id_popup.form_errors.australia_tax_id'),
      canadaFederalTaxId: I18n.t('publisher.billing.vat_id_popup.form_errors.canada_federal_tax_id'),
      canadaProvincialTaxId: I18n.t('publisher.billing.vat_id_popup.form_errors.canada_provincial_tax_id'),
      postalCode: I18n.t('publisher.billing.vat_id_popup.form_errors.postal_code'),
      taxId: I18n.t('publisher.billing.vat_id_popup.form_errors.tax_id')
    }

    invalidFormFields.forEach((field) => {
      if (field === 'canadaProvincialTaxId' && selectedStateProvince === 'QC') {
        setFieldError(field, I18n.t('publisher.billing.vat_id_popup.form_errors.canada_qc_tax_id'))
        return
      }
      setFieldError(
        field,
        errorMessageByField[field]
      )
    })
  }, [invalidFormFields, setFieldError, selectedStateProvince])

  // i18n-tasks-use t('publisher.billing.vat_id_popup.ca_qc_id_label')
  // i18n-tasks-use t('publisher.billing.vat_id_popup.ca_provincial_id_label')
  // i18n-tasks-use t('publisher.billing.vat_id_popup.protips.ca_qc_id')
  // i18n-tasks-use t('publisher.billing.vat_id_popup.protips.ca_provincial_id')
  const taxIdField = () => {
    switch (selectedCountry) {
      case 'AU':
        return (
          <>
            <FullStoryExclude>
              <TextField
                name="australiaTaxId"
                label={(
                  <>
                    {I18n.t('publisher.billing.vat_id_popup.au_tax_id_label')}
                    {' '}
                    <ProTip>
                      {HtmlParser(I18n.t('publisher.billing.vat_id_popup.protips.au_tax_id'))}
                    </ProTip>
                  </>
                )}
                spacingTop="8"
              />
            </FullStoryExclude>
            <AussieText>
              {I18n.t('publisher.billing.vat_id_popup.au_tax_id_disclaimer')}
            </AussieText>
          </>
        )
      case 'CA':
        if (showCanadaProvincialTaxId) {
          const provincialLabel = selectedStateProvince === 'QC' ? 'ca_qc_id_label' : 'ca_provincial_id_label'
          const provincialProtip = selectedStateProvince === 'QC' ? 'ca_qc_id' : 'ca_provincial_id'
          return (
            <HorizontalFieldsWrapper>
              <HorizontalField>
                <FullStoryExclude>
                  <TextField
                    name="canadaFederalTaxId"
                    label={(
                      <>
                        {I18n.t('publisher.billing.vat_id_popup.ca_federal_id_label')}
                        {' '}
                        <ProTip>
                          {I18n.t('publisher.billing.vat_id_popup.protips.ca_federal_id')}
                        </ProTip>
                      </>
                    )}
                    spacingTop="8"
                  />
                </FullStoryExclude>
              </HorizontalField>

              <HorizontalField>
                <FullStoryExclude>
                  <TextField
                    name="canadaProvincialTaxId"
                    label={(
                      <>
                        {I18n.t(`publisher.billing.vat_id_popup.${provincialLabel}`)}
                        {' '}
                        <ProTip>
                          {I18n.t(`publisher.billing.vat_id_popup.protips.${provincialProtip}`)}
                        </ProTip>
                      </>
                    )}
                    spacingTop="8"
                  />
                </FullStoryExclude>
              </HorizontalField>
            </HorizontalFieldsWrapper>
          )
        }

        return (
          <FullStoryExclude>
            <TextField
              name="canadaFederalTaxId"
              label={(
                <>
                  {I18n.t('publisher.billing.vat_id_popup.ca_federal_id_label')}
                  {' '}
                  <ProTip>
                    {I18n.t('publisher.billing.vat_id_popup.protips.ca_federal_id')}
                  </ProTip>
                </>
              )}
              spacingTop="8"
            />
          </FullStoryExclude>
        )
      default:
        return (
          <FullStoryExclude>
            <TextField
              name="taxId"
              label={(
                <>
                  {I18n.t('publisher.billing.vat_id_popup.tax_id_number')}
                  {' '}
                  <ProTip>
                    {I18n.t('publisher.billing.vat_id_popup.protips.tax_id_number')}
                  </ProTip>
                </>
              )}
              spacingTop="8"
              disabled={selectedCountry === 'US'}
            />
          </FullStoryExclude>
        )
    }
  }

  return (
    <>
      <AddressInput />

      <div>
        <FullStoryExclude>
          <TextField
            name="companyName"
            label={(
              <>
                {I18n.t('publisher.billing.vat_id_popup.company_name')}
                {' '}
                <ProTip>
                  {I18n.t('publisher.billing.vat_id_popup.protips.company_name')}
                </ProTip>
              </>
            )}
            spacingTop="8"
            disabled={selectedCountry === 'US'}
          />
        </FullStoryExclude>
      </div>

      <div>
        {taxIdField()}
      </div>

      {
        selectedCountry === 'US' && (
          <Banner
            type="warning"
            spacingTop={8}
          >
            <BodyText>
              {HtmlParser(I18n.t('publisher.billing.tax_exempt.submission_error'))}
            </BodyText>
          </Banner>
        )
      }
    </>
  )
}
